/**
 * HTTP客户端配置
 */

import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from "axios";
import type { ApiConfig, ApiError, Response } from "@/types/api";
import { handleApiError } from "@/utils/errorHandler";

// Token 存储键名
const TOKEN_KEY = "kotori-api-token";

class HttpClient {
  private instance: AxiosInstance;
  private config: ApiConfig;

  constructor(config: ApiConfig) {
    this.config = config;
    this.instance = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout,
      headers: {
        "Content-Type": "application/json",
        ...config.headers,
      },
      withCredentials: config.withCredentials,
    });

    this.setupInterceptors();
  }

  /**
   * 设置拦截器
   */
  private setupInterceptors(): void {
    // 请求拦截器
    this.instance.interceptors.request.use(
      (config) => {
        // 添加 API Token 认证
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // 添加请求时间戳防止缓存
        if (config.method?.toUpperCase() === "GET") {
          config.params = {
            ...config.params,
            _t: Date.now(),
          };
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // 响应拦截器
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        // 统一错误处理
        const apiError: ApiError = {
          error: error.message || "Unknown error",
          code: error.response?.status,
          message: error.response?.data?.error || error.response?.data?.message,
          details: error.response?.data,
        };

        // 检查是否为静默模式
        const silent = error.config?._silent || false;

        // 使用全局错误处理器处理错误
        handleApiError(apiError, silent);

        return Promise.reject(apiError);
      },
    );
  }

  /**
   * 发送请求
   */
  async request<T = any>(config: AxiosRequestConfig): Promise<Response<T>> {
    try {
      const response = await this.instance.request<T>(config);
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers as Record<string, string>,
        config: config as any,
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * GET请求
   */
  async get<T = any>(
    url: string,
    params?: any,
    config?: AxiosRequestConfig,
  ): Promise<Response<T>> {
    return this.request<T>({
      method: "GET",
      url,
      params,
      ...config,
    });
  }

  /**
   * POST请求
   */
  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<Response<T>> {
    return this.request<T>({
      method: "POST",
      url,
      data,
      ...config,
    });
  }

  /**
   * PUT请求
   */
  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<Response<T>> {
    return this.request<T>({
      method: "PUT",
      url,
      data,
      ...config,
    });
  }

  /**
   * DELETE请求
   */
  async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<Response<T>> {
    return this.request<T>({
      method: "DELETE",
      url,
      ...config,
    });
  }

  /**
   * PATCH请求
   */
  async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<Response<T>> {
    return this.request<T>({
      method: "PATCH",
      url,
      data,
      ...config,
    });
  }

  /**
   * 获取当前配置
   */
  getConfig(): ApiConfig {
    return { ...this.config };
  }

  /**
   * 更新配置
   */
  updateConfig(config: Partial<ApiConfig>): void {
    this.config = { ...this.config, ...config };
    this.instance.defaults.baseURL = this.config.baseURL;
    this.instance.defaults.timeout = this.config.timeout;
    this.instance.defaults.headers = {
      ...this.instance.defaults.headers,
      ...this.config.headers,
    };
  }
}

// 默认配置
const defaultConfig: ApiConfig = {
  baseURL: import.meta.env.VITE_API_BASE_URL || "/api",
  timeout: 30000, // 30秒
  withCredentials: false,
};

// 创建全局HTTP客户端实例
export const httpClient = new HttpClient(defaultConfig);

// 导出类型
export type { HttpClient };
export default httpClient;
